import { AuthenticatedTemplate, useMsal } from "@azure/msal-react";
import { AutoAwesome, Diversity1Rounded, MoreHorizRounded } from "@mui/icons-material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Badge,
  Box,
  Button,
  Divider,
  ListItemButton,
  Menu,
  IconButton as MuiIconButton,
  Link as MuiLink,
  ListItemIcon as MuiListItemIcon,
  MenuItem as MuiMenuItem,
  Skeleton,
  Stack,
  Typography,
  keyframes,
  styled,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Avatar, ExpandableDrawer, SideMenuAddButton, SideMenuTeamsList } from "..";
import { HomeIcon } from "../../assets/icons";
import { Links, Paths } from "../../constants";
import { useSession, useSettings } from "../../hooks";
import { SideMenuNotificationButton } from "./SideMenuNotificationButton";
import { useRolesValidation } from "src/http";

const Link = styled(MuiLink)`
  color: ${({ theme }) => theme.palette.text.primary};
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }

  svg {
    fill: ${({ theme }) => theme.palette.text.primary};
  }

  &.active {
    color: ${({ theme }) => theme.palette.primary.main};

    svg {
      fill: ${({ theme }) => theme.palette.primary.main};
    }
  }
`;

const ListItemIcon = styled(MuiListItemIcon)`
  min-width: 24px;
`;

const ListItemText = styled(Typography)<{ $isExpanded: boolean }>`
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  margin-left: ${({ $isExpanded }) => ($isExpanded ? "5px" : 0)};
  overflow: hidden;
  white-space: nowrap;
  font-size: 0.875rem;
  transition: ${({ theme }) => theme.transitions.create(["opacity", "margin"])};
  text-transform: uppercase;
`;

const IconButton = styled(MuiIconButton)<{ $isExpanded: boolean }>`
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  transition: ${({ theme }) => theme.transitions.create("opacity")};
`;

const MenuItem = styled(MuiMenuItem)`
  font-size: 0.875rem;
  text-align: left;
`;

const breatheKeyframes = keyframes`
  0% { opacity: 0.2; }
  50% { opacity: 1; }
  100% { opacity: 0.2; }
`;

const AnimatedBadge = styled(Badge)`
  & .MuiBadge-badge {
    animation: ${breatheKeyframes} 3s ease-in-out infinite;
  }
`;

const SideMenu = () => {
  const { isSessionLoaded, userId, displayName, profilePhotoSmallUrl, endSession } = useSession();
  const { sideMenuSettings, featureFlags } = useSettings();
  const { isEngagementValid, hasPrimaryRole } = useRolesValidation(userId);
  const showRolesWarning = !isEngagementValid || !hasPrimaryRole;
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const { instance } = useMsal();
  const theme = useTheme();

  async function handleSignOutClicked() {
    setProfileMenuAnchor(null);
    endSession();
    instance.logoutRedirect({
      postLogoutRedirectUri: Paths.SignedOut,
    });
  }

  return (
    <>
      <AuthenticatedTemplate>
        <ExpandableDrawer>
          <Stack sx={{ height: "100%", minHeight: "0px", justifyContent: "space-between" }}>
            <Stack
              spacing={0.5}
              divider={<Divider />}
              sx={{
                alignContent: "start",
                pt: 0.5,
                height: "100%",
                minHeight: "0px"
              }}>
              <Stack spacing={0.5} sx={{ p: 0 }}>
                <>
                  {!isSessionLoaded && (
                    <>
                      {[...Array(4)].map((_, i) => (
                        <Stack key={i} direction="row" spacing="8px" sx={{
                          alignItems: "center"
                        }}>
                          <Skeleton width={24} sx={{ marginLeft: "8px !important", minWidth: "24px" }} />
                          <Skeleton width={60} sx={{ overflow: "hidden" }} />
                        </Stack>
                      ))}
                    </>
                  )}

                  {isSessionLoaded && (
                    <>
                      <Link href={Paths.Home}>
                        <ListItemButton sx={{ p: 0, borderRadius: "8px" }}>
                          <ListItemIcon>
                            <HomeIcon
                              style={{
                                height: 24,
                                width: 24,
                                margin: "8px",
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText $isExpanded={sideMenuSettings.isExpanded}>Home</ListItemText>
                        </ListItemButton>
                      </Link>
                      <Link href={Paths.Search}>
                        <ListItemButton sx={{ p: 0, borderRadius: "8px" }}>
                          <ListItemIcon>
                            <SearchRoundedIcon
                              style={{
                                height: 24,
                                width: 24,
                                margin: "8px",
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText $isExpanded={sideMenuSettings.isExpanded}>Search</ListItemText>
                        </ListItemButton>
                      </Link>
                      <Link href={Paths.Chat}>
                        <ListItemButton sx={{ p: 0, borderRadius: "8px" }}>
                          <ListItemIcon>
                            <AutoAwesome
                              style={{
                                height: 24,
                                width: 24,
                                margin: "8px",
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText $isExpanded={sideMenuSettings.isExpanded}>COD-E</ListItemText>
                        </ListItemButton>
                      </Link>
                      <Link href={Paths.CoherenceReport}>
                        <ListItemButton sx={{ p: 0, borderRadius: "8px" }}>
                          <ListItemIcon>
                            <Diversity1Rounded
                              style={{
                                height: 24,
                                width: 24,
                                margin: "8px",
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText $isExpanded={sideMenuSettings.isExpanded}>Coherence</ListItemText>
                        </ListItemButton>
                      </Link>
                      <ListItemButton
                        sx={{ p: 0, borderRadius: "8px" }}
                        onClick={(event) => setMoreMenuAnchor(event.currentTarget)}
                      >
                        <ListItemIcon sx={{ color: theme.palette.text.primary }}>
                          <MoreHorizRounded
                            style={{
                              height: 24,
                              width: 24,
                              margin: "8px",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText $isExpanded={sideMenuSettings.isExpanded}>More</ListItemText>
                      </ListItemButton>

                      <Menu
                        anchorEl={moreMenuAnchor}
                        open={Boolean(moreMenuAnchor)}
                        sx={{ m: 0.5 }}
                        onClose={() => setMoreMenuAnchor(null)}
                        anchorOrigin={{ vertical: "center", horizontal: "right" }}
                        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                      >
                        <MenuItem
                          onClick={() => {
                            setMoreMenuAnchor(null);
                            navigate(Paths.Guide);
                          }}
                        >
                          CODE OS Guide
                        </MenuItem>
                        {featureFlags.surveys && (
                          <MenuItem
                            onClick={() => {
                              setMoreMenuAnchor(null);
                              navigate(Paths.Surveys);
                            }}
                          >
                            Surveys
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => {
                            setMoreMenuAnchor(null);
                            navigate(Paths.Reports);
                          }}
                        >
                          Reports
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </>
              </Stack>

              {isSessionLoaded && <SideMenuTeamsList />}
            </Stack>

            <Divider sx={{ mb: 0.5 }} />

            <Stack spacing={1} sx={{
              alignItems: "start"
            }}>
              <SideMenuNotificationButton />
              <SideMenuAddButton />

              {!isSessionLoaded ? (
                <Skeleton variant="circular" width="40px" />
              ) : (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%"
                  }}>
                  {sideMenuSettings.isExpanded ? (
                    <Link href={Links.User(userId!)}>
                      <AnimatedBadge
                        variant="dot"
                        color="warning"
                        overlap="circular"
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        invisible={!showRolesWarning}
                        sx={{ ml: "4px" }}
                      >
                        <Avatar
                          user={{
                            displayName: displayName,
                            userId: userId,
                            profilePhotoSmallUrl: profilePhotoSmallUrl,
                            enabled: true,
                          }}
                        />
                      </AnimatedBadge>
                    </Link>
                  ) : (
                    <Button
                      onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
                      disableRipple
                      sx={{ p: 0, width: 40, minWidth: 40, mr: "8px" }}
                    >
                      <AnimatedBadge
                        variant="dot"
                        color="warning"
                        overlap="circular"
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        invisible={!showRolesWarning}
                      >
                        <Avatar
                          user={{
                            displayName: displayName,
                            userId: userId,
                            profilePhotoSmallUrl: profilePhotoSmallUrl,
                            enabled: true,
                          }}
                        />
                      </AnimatedBadge>
                    </Button>
                  )}

                  <Link href={Links.User(userId!)} sx={{ minWidth: 0 }}>
                    <ListItemText sx={{ m: 0 }} noWrap $isExpanded={sideMenuSettings.isExpanded}>
                      {displayName?.split(" ")[0]}
                    </ListItemText>
                  </Link>

                  <Box sx={{ flexGrow: 1 }}></Box>

                  <IconButton
                    edge="end"
                    sx={{ mr: 0.5 }}
                    $isExpanded={sideMenuSettings.isExpanded}
                    onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
                  >
                    <ExpandMoreRoundedIcon
                      style={{
                        height: 24,
                        width: 24,
                      }}
                    />
                  </IconButton>

                  <Menu
                    anchorEl={profileMenuAnchor}
                    open={Boolean(profileMenuAnchor)}
                    sx={{ m: 0.5 }}
                    onClose={() => setProfileMenuAnchor(null)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                  >
                    <MenuItem
                      onClick={() => {
                        setProfileMenuAnchor(null);
                        navigate(Links.User(userId!));
                      }}
                    >
                      Profile
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setProfileMenuAnchor(null);
                        navigate(Links.UserStrategy(userId!));
                      }}
                    >
                      Strategy
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setProfileMenuAnchor(null);
                        navigate(Links.UserImplementation(userId!));
                      }}
                    >
                      Implementation
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setProfileMenuAnchor(null);
                        navigate(Links.UserMeetings(userId!));
                      }}
                    >
                      Meetings
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setProfileMenuAnchor(null);
                        navigate(Links.UserFeedback(userId!));
                      }}
                    >
                      Feedback
                    </MenuItem>
                    <MenuItem
                      divider
                      onClick={() => {
                        setProfileMenuAnchor(null);
                        navigate(Links.UserMetrics(userId!));
                      }}
                    >
                      Metrics
                    </MenuItem>
                    <MenuItem
                      divider
                      onClick={() => {
                        setProfileMenuAnchor(null);
                        navigate(Links.UserSettings());
                      }}
                    >
                      Settings
                    </MenuItem>
                    <MenuItem onClick={handleSignOutClicked}>Sign Out</MenuItem>
                  </Menu>
                </Stack>
              )}
            </Stack>
          </Stack>
        </ExpandableDrawer>
      </AuthenticatedTemplate>
    </>
  );
};

export { SideMenu };
